import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import ImageUpload from '../components/ImageUpload';
import CustomSelect from '../components/CustomSelect';
import api from '../utils/api';
import { getCityFromHost } from '../utils/getCityFromHost';
import { showSuccess, showError } from '../utils/toast';
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  CheckCircle2,
  TrendingUp,
  Users,
  Search,
  Store,
  LayoutGrid,
  Image as ImageIcon,
  MessageCircle,
  Clock,
  User,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  Check,
  Share2,
  Compass,
  AlertCircle,
  HelpCircle
} from 'lucide-react';

const CITY_PINCODES = {
  fatehabad: '125050',
  tohana: '125120',
  ratia: '125051',
  bhuna: '125111',
  bhattu: '125053',
  jakhal: '125123',
  uklana: '125113',
  ukalana: '125113',
  hisar: '125001',
  sirsa: '125055'
};

const STEPS = [
  { id: 1, title: 'बुनियादी जानकारी', subtitle: 'Basic Details', icon: Building2 },
  { id: 2, title: 'संपर्क व समय', subtitle: 'Contact & Timings', icon: Phone },
  { id: 3, title: 'स्थान व पता', subtitle: 'Location & Address', icon: MapPin },
  { id: 4, title: 'फोटो व विवरण', subtitle: 'Photos & Info', icon: Sparkles }
];

const SubmitBusiness = () => {
  const navigate = useNavigate();
  const city = getCityFromHost();

  const [currentStep, setCurrentStep] = useState(1);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedBusiness, setSubmittedBusiness] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    contactPerson: '',
    category: '',
    subcategory: '',
    phone: '',
    whatsapp: '',
    sameAsPhone: true,
    email: '',
    website: '',
    openingTime: '09:00 AM',
    closingTime: '08:00 PM',
    isOpenSunday: true,
    city: '',
    village: '',
    landmark: '',
    address: '',
    pincode: '',
    description: '',
  });

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [villages, setVillages] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stepErrors, setStepErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catsRes, citiesRes, villagesRes] = await Promise.all([
          api.get('/categories'),
          api.get('/cities'),
          api.get('/villages'),
        ]);
        setCategories(catsRes.data || []);
        setCities(citiesRes.data || []);
        setVillages((villagesRes.data || []).map(v => ({
          _id: v._id,
          name: `${v.name} (${v.block} Block)`
        })));

        let detectedCityId = '';
        let detectedSlug = '';
        if (city) {
          const detectedCity = (citiesRes.data || []).find(
            c => c.domain === city || c.slug === city
          );
          if (detectedCity) {
            detectedCityId = detectedCity._id;
            detectedSlug = detectedCity.slug;
          }
        }

        setFormData(prev => ({
          ...prev,
          city: detectedCityId || prev.city,
          pincode: (detectedSlug && CITY_PINCODES[detectedSlug]) || prev.pincode || '125050'
        }));
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [city]);

  useEffect(() => {
    if (formData.category) {
      const fetchSubcategories = async () => {
        try {
          const response = await api.get(`/subcategories?categoryId=${formData.category}`);
          setSubcategories(response.data || []);
        } catch (error) {
          console.error('Error fetching subcategories:', error);
          setSubcategories([]);
        }
      };
      fetchSubcategories();
    } else {
      setSubcategories([]);
    }
  }, [formData.category]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const updated = { ...prev, [name]: val };

      if (name === 'sameAsPhone' && val) {
        updated.whatsapp = updated.phone;
      }
      if (name === 'phone' && prev.sameAsPhone) {
        updated.whatsapp = value;
      }

      if (name === 'city') {
        const foundCity = cities.find(c => c._id === value);
        if (foundCity && CITY_PINCODES[foundCity.slug]) {
          updated.pincode = CITY_PINCODES[foundCity.slug];
        }
      }

      return updated;
    });

    if (stepErrors[name]) {
      setStepErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateStep = (step) => {
    const errors = {};

    if (step === 1) {
      if (!formData.name.trim()) errors.name = 'व्यवसाय / दुकान का नाम आवश्यक है (Business Name is required)';
      if (!formData.category) errors.category = 'कृपया मुख्य श्रेणी चुनें (Category is required)';
    }

    if (step === 2) {
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (!phoneDigits) {
        errors.phone = 'फ़ोन नंबर आवश्यक है (Phone number is required)';
      } else if (phoneDigits.length !== 10) {
        errors.phone = 'कृपया 10 अंकों का मान्य मोबाइल नंबर दर्ज करें (Valid 10-digit mobile number required)';
      }

      if (!formData.sameAsPhone && formData.whatsapp) {
        const waDigits = formData.whatsapp.replace(/\D/g, '');
        if (waDigits.length !== 10) {
          errors.whatsapp = 'कृपया 10 अंकों का मान्य व्हाट्सएप नंबर दर्ज करें';
        }
      }
    }

    if (step === 3) {
      if (!formData.city && !city) errors.city = 'कृपया शहर चुनें (City is required)';
      if (!formData.address.trim()) errors.address = 'दुकान या कार्यालय का पता आवश्यक है (Address is required)';
    }

    setStepErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
      window.scrollTo({ top: 300, behavior: 'smooth' });
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateStep(1) || !validateStep(2) || !validateStep(3)) {
      showError('कृपया सभी आवश्यक फ़ील्ड्स सही ढंग से भरें');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        name: formData.name.trim(),
        contactPerson: formData.contactPerson.trim(),
        category: formData.category,
        subcategory: formData.subcategory || null,
        city: formData.city,
        village: formData.village || null,
        address: formData.address.trim(),
        landmark: formData.landmark.trim(),
        pincode: formData.pincode.trim(),
        phone: formData.phone.replace(/\D/g, ''),
        whatsapp: (formData.sameAsPhone ? formData.phone : formData.whatsapp).replace(/\D/g, ''),
        email: formData.email.trim(),
        website: formData.website.trim(),
        description: formData.description.trim(),
        timings: {
          opening: formData.openingTime,
          closing: formData.closingTime,
          isOpenSunday: formData.isOpenSunday,
          closedOn: formData.isOpenSunday ? '' : 'Sunday'
        }
      };

      const response = await api.post('/businesses', payload);
      const createdBiz = response.data;
      const businessId = createdBiz._id;

      if (images.length > 0) {
        const imageFormData = new FormData();
        images.forEach((img) => {
          if (img.file) {
            imageFormData.append('images', img.file);
          }
        });

        try {
          await api.post(`/businesses/${businessId}/images`, imageFormData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (imgError) {
          console.error('Error uploading images:', imgError);
        }
      }

      setSubmittedBusiness(createdBiz);
      setIsSuccess(true);
      showSuccess('बधाई! आपका बिज़नेस सफलतापूर्वक दर्ज कर लिया गया है।');
      window.scrollTo({ top: 200, behavior: 'smooth' });
    } catch (error) {
      console.error('Error submitting business:', error);
      showError(error.response?.data?.message || 'Error submitting business. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleShareWhatsApp = () => {
    const text = `मैंने अपना व्यवसाय *${formData.name}* Digital Fatehabad पोर्टल पर लिस्ट कर दिया है! आप भी अपनी दुकान या सर्विस को मुफ़्त में जोड़ें: https://digitalfatehabad.com`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleResetAndNew = () => {
    setIsSuccess(false);
    setSubmittedBusiness(null);
    setCurrentStep(1);
    setFormData(prev => ({
      name: '',
      contactPerson: '',
      category: '',
      subcategory: '',
      phone: '',
      whatsapp: '',
      sameAsPhone: true,
      email: '',
      website: '',
      openingTime: '09:00 AM',
      closingTime: '08:00 PM',
      isOpenSunday: true,
      city: prev.city,
      village: '',
      landmark: '',
      address: '',
      pincode: prev.pincode,
      description: ''
    }));
    setImages([]);
    setStepErrors({});
    window.scrollTo({ top: 200, behavior: 'smooth' });
  };

  const selectedCatObj = categories.find(c => c._id === formData.category);
  const selectedSubObj = subcategories.find(s => s._id === formData.subcategory);
  const selectedCityObj = cities.find(c => c._id === formData.city);

  const benefits = [
    {
      icon: <Users className="w-5 h-5 text-blue-500" />,
      title: "Reach Local Customers",
      description: "हज़ारों स्थानीय ग्राहकों तक सीधे पहुँचें जो रोज़ाना आपके शहर में सेवाएँ खोजते हैं।"
    },
    {
      icon: <TrendingUp className="w-5 h-5 text-green-500" />,
      title: "Boost Online Visibility",
      description: "Google सर्च और डिजिटल डायरेक्टरी पर अपनी दुकान की ऑनलाइन उपस्थिति मजबूत करें।"
    },
    {
      icon: <CheckCircle2 className="w-5 h-5 text-purple-500" />,
      title: "Free Verified Listing",
      description: "100% मुफ़्त लिस्टिंग और डिजिटल पहचान से ग्राहकों का विश्वास हासिल करें।"
    }
  ];

  return (
    <>
      <SEO
        title="दुकान / बिज़नेस जोड़ें - Digital Fatehabad Directory"
        description="Join Fatehabad's largest digital directory. Submit your business for free and reach more customers."
        canonical="/submit-business"
      />

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-20">
        {/* Hero Header */}
        <div className="bg-gradient-to-r from-gray-900 via-primary-950 to-gray-900 text-white py-14 lg:py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 to-purple-600/20" />
          <div className="container-custom relative z-10 text-center px-4">
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-primary-500/20 text-primary-300 border border-primary-500/30 text-xs font-semibold mb-4 backdrop-blur-sm">
              <Store className="w-3.5 h-3.5 text-yellow-300" />
              100% Free Business Listing Portal
            </span>
            <h1 className="text-3xl md:text-5xl font-black mb-4 tracking-tight">
              अपनी दुकान या व्यवसाय को <br className="hidden sm:inline" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-400 via-yellow-300 to-primary-300">
                Digital Fatehabad
              </span> पर जोड़ें
            </h1>
            <p className="text-base sm:text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
              फतेहाबाद और आसपास के पूरे इलाके में अपना व्यापार बढ़ाएं। 2 मिनट में अपनी दुकान मुफ़्त में लिस्ट करें।
            </p>
          </div>
        </div>

        <div className="container-custom -mt-8 relative z-20 px-4">
          <div className="flex flex-col lg:flex-row gap-8">
            
            {/* Left/Main Column: Wizard Form or Success Screen */}
            <div className="lg:w-2/3">
              <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-700">
                
                {isSuccess ? (
                  /* Success Celebration View */
                  <div className="p-8 sm:p-12 text-center flex flex-col items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 flex items-center justify-center mb-6 animate-bounce shadow-lg">
                      <CheckCircle2 className="w-12 h-12" />
                    </div>

                    <h2 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-2">
                      बधाई हो! लिस्टिंग दर्ज हो गई है 🎉
                    </h2>
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 max-w-md mb-6 leading-relaxed">
                      <strong className="text-primary-600 dark:text-primary-400 font-bold">{formData.name}</strong> का विवरण सफलतापूर्वक प्राप्त हो गया है। एडमिन समीक्षा के बाद यह शीघ्र ही पोर्टल पर लाइव हो जाएगा।
                    </p>

                    <div className="bg-gray-50 dark:bg-gray-800/60 rounded-2xl p-4 max-w-md w-full border border-gray-200 dark:border-gray-700 text-left mb-8 space-y-2 text-sm">
                      <div className="flex justify-between text-gray-600 dark:text-gray-400">
                        <span>श्रेणी (Category):</span>
                        <span className="font-semibold text-gray-900 dark:text-white">{selectedCatObj?.name || 'Local Service'}</span>
                      </div>
                      <div className="flex justify-between text-gray-600 dark:text-gray-400">
                        <span>फ़ोन नंबर:</span>
                        <span className="font-semibold text-gray-900 dark:text-white">+91 {formData.phone}</span>
                      </div>
                      <div className="flex justify-between text-gray-600 dark:text-gray-400">
                        <span>स्थान (City/Town):</span>
                        <span className="font-semibold text-gray-900 dark:text-white">{selectedCityObj?.name || 'Fatehabad'}</span>
                      </div>
                      {formData.landmark && (
                        <div className="flex justify-between text-gray-600 dark:text-gray-400">
                          <span>लैंडमार्क:</span>
                          <span className="font-semibold text-gray-900 dark:text-white">{formData.landmark}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
                      <button
                        onClick={handleShareWhatsApp}
                        className="flex-1 py-3 px-5 bg-[#25D366] hover:bg-[#20ba59] text-white font-bold rounded-2xl flex items-center justify-center gap-2 transition-all shadow-md active:scale-95 text-sm"
                      >
                        <Share2 className="w-4 h-4" /> WhatsApp पर शेयर करें
                      </button>
                      <button
                        onClick={handleResetAndNew}
                        className="py-3 px-5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold rounded-2xl transition-all text-sm"
                      >
                        अन्य दुकान जोड़ें
                      </button>
                    </div>

                    <button
                      onClick={() => navigate('/')}
                      className="mt-4 text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      ← मुख्य पृष्ठ (Homepage) पर जाएं
                    </button>
                  </div>
                ) : (
                  <>
                    {/* Stepper Header */}
                    <div className="px-6 py-5 bg-gray-50/80 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-700">
                      <div className="flex items-center justify-between max-w-xl mx-auto relative">
                        {/* Progress Bar Background */}
                        <div className="absolute top-1/2 left-0 right-0 -translate-y-1/2 h-1 bg-gray-200 dark:bg-gray-700 -z-0 rounded-full" />
                        <div
                          className="absolute top-1/2 left-0 -translate-y-1/2 h-1 bg-primary-600 transition-all duration-500 rounded-full"
                          style={{ width: `${((currentStep - 1) / (STEPS.length - 1)) * 100}%` }}
                        />

                        {STEPS.map((step) => {
                          const isCompleted = currentStep > step.id;
                          const isCurrent = currentStep === step.id;
                          const Icon = step.icon;

                          return (
                            <div key={step.id} className="relative z-10 flex flex-col items-center">
                              <button
                                type="button"
                                onClick={() => {
                                  if (isCompleted) setCurrentStep(step.id);
                                }}
                                disabled={!isCompleted && !isCurrent}
                                className={`w-10 h-10 sm:w-11 sm:h-11 rounded-2xl flex items-center justify-center font-bold text-xs transition-all duration-300 shadow-sm ${
                                  isCompleted
                                    ? 'bg-green-600 text-white cursor-pointer hover:scale-105'
                                    : isCurrent
                                    ? 'bg-primary-600 text-white ring-4 ring-primary-100 dark:ring-primary-950 scale-105'
                                    : 'bg-white dark:bg-gray-800 text-gray-400 border border-gray-200 dark:border-gray-700'
                                }`}
                              >
                                {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                              </button>
                              <span className={`text-[11px] font-semibold mt-1 hidden sm:block ${
                                isCurrent ? 'text-primary-600 dark:text-primary-400' : 'text-gray-500'
                              }`}>
                                {step.title}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Step Form Body */}
                    <div className="p-6 sm:p-8">
                      {/* STEP 1: Basic Details */}
                      {currentStep === 1 && (
                        <div className="space-y-6">
                          <div>
                            <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                              दुकान / व्यवसाय का नाम (Business Name) *
                            </label>
                            <div className="relative">
                              <Store className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="उदा. अमन ऑटो स्पेयर पार्ट्स, शर्मा मिष्ठान भंडार"
                                className={`w-full pl-10 pr-4 py-3 rounded-2xl text-sm bg-gray-50 dark:bg-gray-900 border text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${
                                  stepErrors.name ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200 dark:border-gray-700'
                                }`}
                              />
                            </div>
                            {stepErrors.name && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle className="w-3.5 h-3.5" /> {stepErrors.name}
                              </p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                              मालिक / संपर्क व्यक्ति का नाम (Owner / Contact Person)
                            </label>
                            <div className="relative">
                              <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                type="text"
                                name="contactPerson"
                                value={formData.contactPerson}
                                onChange={handleChange}
                                placeholder="उदा. राजेश कुमार, डॉ. सचिन मेहता"
                                className="w-full pl-10 pr-4 py-3 rounded-2xl text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                              />
                            </div>
                            <p className="text-[11px] text-gray-500 mt-1">स्थानीय लोग अक्सर दुकान को मालिक के नाम से भी पहचानते हैं।</p>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                                मुख्य श्रेणी (Main Category) *
                              </label>
                              <CustomSelect
                                name="category"
                                value={formData.category}
                                onChange={handleChange}
                                options={categories}
                                placeholder="श्रेणी चुनें (उदा. Automotive, Doctors)"
                                required
                                icon={LayoutGrid}
                                className={stepErrors.category ? 'border-red-500' : ''}
                              />
                              {stepErrors.category && (
                                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                  <AlertCircle className="w-3.5 h-3.5" /> {stepErrors.category}
                                </p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                                उप-श्रेणी (Subcategory)
                              </label>
                              <CustomSelect
                                name="subcategory"
                                value={formData.subcategory}
                                onChange={handleChange}
                                options={subcategories}
                                placeholder={formData.category ? (subcategories.length ? 'उप-श्रेणी चुनें' : 'कोई उप-श्रेणी नहीं') : 'पहले मुख्य श्रेणी चुनें'}
                                disabled={!formData.category}
                                icon={Search}
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* STEP 2: Contact & Timings */}
                      {currentStep === 2 && (
                        <div className="space-y-6">
                          <div>
                            <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                              फ़ोन / मोबाइल नंबर (Calling Phone Number) *
                            </label>
                            <div className="relative">
                              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-bold text-gray-400">
                                +91
                              </span>
                              <input
                                type="tel"
                                name="phone"
                                maxLength={10}
                                value={formData.phone}
                                onChange={handleChange}
                                placeholder="98XXXXXXXX (10 अंक)"
                                className={`w-full pl-14 pr-10 py-3 rounded-2xl text-sm bg-gray-50 dark:bg-gray-900 border text-gray-900 dark:text-white font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${
                                  stepErrors.phone ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200 dark:border-gray-700'
                                }`}
                              />
                              {formData.phone.replace(/\D/g, '').length === 10 && (
                                <Check className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                              )}
                            </div>
                            {stepErrors.phone && (
                              <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                <AlertCircle className="w-3.5 h-3.5" /> {stepErrors.phone}
                              </p>
                            )}
                          </div>

                          {/* WhatsApp Support Toggle */}
                          <div className="p-4 rounded-2xl bg-green-50/70 dark:bg-green-950/20 border border-green-200 dark:border-green-900/40 space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2 text-sm font-bold text-green-800 dark:text-green-300">
                                <MessageCircle className="w-4 h-4 text-green-600" />
                                <span>व्हाट्सएप नंबर (WhatsApp Support)</span>
                              </div>
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  name="sameAsPhone"
                                  checked={formData.sameAsPhone}
                                  onChange={handleChange}
                                  className="sr-only peer"
                                />
                                <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
                              </label>
                            </div>

                            {formData.sameAsPhone ? (
                              <p className="text-xs text-green-700 dark:text-green-400">
                                ✓ कॉलिंग नंबर (+91 {formData.phone || '...'}) ही व्हाट्सएप नंबर के रूप में उपयोग होगा।
                              </p>
                            ) : (
                              <div>
                                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                  अलग व्हाट्सएप नंबर दर्ज करें
                                </label>
                                <input
                                  type="tel"
                                  name="whatsapp"
                                  maxLength={10}
                                  value={formData.whatsapp}
                                  onChange={handleChange}
                                  placeholder="अलग 10-अंकीय व्हाट्सएप नंबर"
                                  className="w-full px-4 py-2.5 rounded-xl text-sm bg-white dark:bg-gray-800 border border-green-300 dark:border-green-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                                />
                              </div>
                            )}
                          </div>

                          {/* Timings */}
                          <div className="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 space-y-3">
                            <div className="flex items-center gap-2 text-sm font-bold text-gray-800 dark:text-gray-200">
                              <Clock className="w-4 h-4 text-primary-500" />
                              <span>दुकान खुलने व बंद होने का समय (Business Hours)</span>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">खुलने का समय (Opening)</label>
                                <select
                                  name="openingTime"
                                  value={formData.openingTime}
                                  onChange={handleChange}
                                  className="w-full px-3 py-2 text-xs sm:text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none"
                                >
                                  {['06:00 AM', '07:00 AM', '08:00 AM', '09:00 AM', '10:00 AM', '11:00 AM'].map(t => (
                                    <option key={t} value={t}>{t}</option>
                                  ))}
                                </select>
                              </div>

                              <div>
                                <label className="block text-xs text-gray-500 mb-1">बंद होने का समय (Closing)</label>
                                <select
                                  name="closingTime"
                                  value={formData.closingTime}
                                  onChange={handleChange}
                                  className="w-full px-3 py-2 text-xs sm:text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none"
                                >
                                  {['06:00 PM', '07:00 PM', '08:00 PM', '09:00 PM', '10:00 PM', '11:00 PM'].map(t => (
                                    <option key={t} value={t}>{t}</option>
                                  ))}
                                </select>
                              </div>
                            </div>

                            <div className="flex items-center justify-between pt-1">
                              <span className="text-xs text-gray-700 dark:text-gray-300 font-medium">
                                रविवार को दुकान खुली रहती है? (Open on Sunday?)
                              </span>
                              <input
                                type="checkbox"
                                name="isOpenSunday"
                                checked={formData.isOpenSunday}
                                onChange={handleChange}
                                className="w-4 h-4 text-primary-600 rounded"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                                ईमेल (Email - Optional)
                              </label>
                              <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                placeholder="shop@example.com"
                                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                                वेबसाइट (Website - Optional)
                              </label>
                              <input
                                type="url"
                                name="website"
                                value={formData.website}
                                onChange={handleChange}
                                placeholder="https://..."
                                className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* STEP 3: Location & Address */}
                      {currentStep === 3 && (
                        <div className="space-y-6">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                                शहर / कस्बा (City / Town) *
                              </label>
                              <CustomSelect
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                options={cities}
                                placeholder={city ? `Auto-detected: ${city}` : 'शहर चुनें'}
                                required={!city}
                                disabled={!!city}
                                icon={MapPin}
                                className={stepErrors.city ? 'border-red-500' : ''}
                              />
                              {stepErrors.city && (
                                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                  <AlertCircle className="w-3.5 h-3.5" /> {stepErrors.city}
                                </p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                                गांव (Village - यदि दुकान गांव में है)
                              </label>
                              <CustomSelect
                                name="village"
                                value={formData.village}
                                onChange={handleChange}
                                options={villages}
                                placeholder="गांव चुनें (वैकल्पिक)"
                                icon={Compass}
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                              नजदीकी लैंडमार्क / मार्केट (Landmark / Market Area)
                            </label>
                            <input
                              type="text"
                              name="landmark"
                              value={formData.landmark}
                              onChange={handleChange}
                              placeholder="उदा. पुरानी अनाज मंडी के पास, बस स्टैंड के सामने, जीटी रोड"
                              className="w-full px-4 py-3 rounded-2xl text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                            <p className="text-[11px] text-gray-500 mt-1">लैंडमार्क से ग्राहकों को आपकी दुकान ढूँढने में आसानी होती है।</p>
                          </div>

                          <div className="grid grid-cols-3 gap-3">
                            <div className="col-span-2">
                              <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                                पूरा पता (Full Address) *
                              </label>
                              <textarea
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                placeholder="दुकान नंबर, गली, मोहल्ला, रोड..."
                                rows={2}
                                className={`w-full px-4 py-2.5 rounded-2xl text-sm bg-gray-50 dark:bg-gray-900 border text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                                  stepErrors.address ? 'border-red-500 ring-1 ring-red-500' : 'border-gray-200 dark:border-gray-700'
                                }`}
                              />
                              {stepErrors.address && (
                                <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                                  <AlertCircle className="w-3.5 h-3.5" /> {stepErrors.address}
                                </p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                                पिनकोड (Pincode)
                              </label>
                              <input
                                type="text"
                                name="pincode"
                                maxLength={6}
                                value={formData.pincode}
                                onChange={handleChange}
                                placeholder="125050"
                                className="w-full px-4 py-2.5 rounded-2xl text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
                              />
                            </div>
                          </div>
                        </div>
                      )}

                      {/* STEP 4: Photos & Preview */}
                      {currentStep === 4 && (
                        <div className="space-y-6">
                          <div>
                            <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">
                              व्यवसाय का विवरण (About Business / Services)
                            </label>
                            <textarea
                              name="description"
                              value={formData.description}
                              onChange={handleChange}
                              rows={3}
                              placeholder="अपनी दुकान के मुख्य उत्पादों या सेवाओं का विवरण लिखें..."
                              className="w-full px-4 py-2.5 rounded-2xl text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-bold text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-2">
                              <ImageIcon className="w-4 h-4 text-primary-500" />
                              <span>दुकान या बोर्ड की फोटो (Upload Photos - Optional)</span>
                            </label>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                              अधिकतम 10 फोटो अपलोड कर सकते हैं। पहली फोटो मुख्य कार्ड पर दिखेगी।
                            </p>
                            <ImageUpload
                              images={images}
                              onImagesChange={setImages}
                              maxImages={10}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Step Navigation Footer */}
                    <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3">
                      {currentStep > 1 ? (
                        <button
                          type="button"
                          onClick={handlePrev}
                          disabled={loading}
                          className="px-5 py-2.5 rounded-2xl text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 transition-all flex items-center gap-1.5"
                        >
                          <ArrowLeft className="w-4 h-4" /> Back
                        </button>
                      ) : (
                        <div />
                      )}

                      {currentStep < 4 ? (
                        <button
                          type="button"
                          onClick={handleNext}
                          className="px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-2xl text-sm font-bold shadow-md hover:shadow-lg transition-all flex items-center gap-1.5 active:scale-95"
                        >
                          <span>Next</span>
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleSubmit}
                          disabled={loading}
                          className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-2xl text-sm font-black shadow-lg hover:shadow-xl transition-all flex items-center gap-2 active:scale-95 disabled:opacity-50"
                        >
                          {loading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                              <span>दर्ज हो रहा है...</span>
                            </>
                          ) : (
                            <>
                              <Check className="w-5 h-5" />
                              <span>सबमिट करें (Confirm & Submit)</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Right Column: Dynamic Live Preview Card + Benefits */}
            <div className="lg:w-1/3 space-y-6">
              
              {/* Dynamic Live Card Preview */}
              <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-md border border-gray-100 dark:border-gray-700">
                <div className="text-xs font-bold uppercase tracking-wider text-primary-600 dark:text-primary-400 mb-3 flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4" /> लाइव कार्ड प्रीव्यू (Live Preview)
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  जैसे-जैसे आप फ़ॉर्म भरेंगे, आपका कार्ड इस तरह ग्राहकों को दिखेगा:
                </p>

                <div className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-4 border border-gray-200 dark:border-gray-700 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-primary-50 dark:bg-primary-950/60 text-primary-600 dark:text-primary-300 border border-primary-200/50">
                        {selectedCatObj?.name || 'श्रेणी (Category)'}
                      </span>
                      {selectedSubObj && (
                        <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                          {selectedSubObj.name}
                        </span>
                      )}
                    </div>
                    <span className="text-[11px] font-semibold text-green-600 bg-green-50 dark:bg-green-950/50 px-2 py-0.5 rounded-full flex items-center gap-1 flex-shrink-0">
                      <Clock className="w-3 h-3" /> {formData.isOpenSunday ? 'Sun Open' : 'Closed Sun'}
                    </span>
                  </div>

                  <div>
                    <h4 className="text-base font-black text-gray-900 dark:text-white leading-tight">
                      {formData.name || 'आपकी दुकान / व्यवसाय का नाम'}
                    </h4>
                    {formData.contactPerson && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-medium mt-0.5">
                        सम्पर्क: {formData.contactPerson}
                      </p>
                    )}
                  </div>

                  <div className="text-xs text-gray-600 dark:text-gray-400 flex items-start gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-primary-500 flex-shrink-0 mt-0.5" />
                    <span className="line-clamp-2">
                      {[formData.landmark, formData.address, selectedCityObj?.name].filter(Boolean).join(', ') || 'फतेहाबाद, हरियाणा'}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-200 dark:border-gray-800 text-xs">
                    <div className="flex items-center gap-1 font-bold text-gray-800 dark:text-gray-200">
                      <Phone className="w-3.5 h-3.5 text-primary-500" />
                      <span>{formData.phone ? `+91 ${formData.phone}` : '+91 98XXXXXXXX'}</span>
                    </div>
                    <div className="flex items-center gap-1 font-bold text-green-600 justify-end">
                      <MessageCircle className="w-3.5 h-3.5" />
                      <span>WhatsApp Active</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Benefits Box */}
              <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">पोर्टल पर जुड़ने के फ़ायदे</h3>
                <div className="space-y-4">
                  {benefits.map((item, index) => (
                    <div key={index} className="flex gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gray-50 dark:bg-gray-700 flex items-center justify-center">
                        {item.icon}
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{item.title}</h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Support Contact */}
              <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-3xl p-6 text-white shadow-lg">
                <div className="flex items-center gap-2 mb-2">
                  <HelpCircle className="w-5 h-5 text-yellow-300" />
                  <h3 className="text-base font-bold">कोई सहायता चाहिए?</h3>
                </div>
                <p className="text-xs text-primary-100 mb-4 leading-relaxed">
                  यदि आपको फ़ॉर्म भरने में कोई समस्या आ रही है, तो हमारी सपोर्ट टीम से संपर्क करें।
                </p>
                <a
                  href="mailto:support@digitalfatehabad.com"
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors px-4 py-2 rounded-xl backdrop-blur-sm text-xs font-semibold"
                >
                  <Mail className="w-3.5 h-3.5" /> support@digitalfatehabad.com
                </a>
              </div>

            </div>

          </div>
        </div>
      </div>
    </>
  );
};

export default SubmitBusiness;
